import { Component } from "react";
import { GlobalErrorFallback } from "./GlobalErrorFallback";

/**
 * GlobalErrorBoundary
 *
 * Level 1 — App-wide safety net.
 * Placed in index.jsx, outside the Redux Provider.
 * Catches catastrophic failures (Provider crashes, JS runtime errors
 * that escape all lower-level boundaries).
 *
 * Usage:
 *   <GlobalErrorBoundary>
 *     <Provider store={store}>
 *       <AppRoutes />
 *     </Provider>
 *   </GlobalErrorBoundary>
 */
class GlobalErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so next render shows the fallback UI.
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Ideal hook for external error reporting (e.g. Sentry).
    // Example: Sentry.captureException(error, { extra: errorInfo });
    console.error("[GlobalErrorBoundary] Uncaught error:", error);
    console.error("[GlobalErrorBoundary] Component stack:", errorInfo.componentStack);

    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return <GlobalErrorFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}

export { GlobalErrorBoundary };
